<script lang="ts">
import type { AcceptableValue, RadioGroupRootEmits, RadioGroupRootProps } from 'reka-ui'
import type { Component } from 'vue'
import type { Color } from '../styles/colors'

export type RadioGroupValue = AcceptableValue
export type RadioGroupItem = {
  label?: string
  description?: string
  disabled?: boolean
  value?: RadioGroupValue
  class?: any
  [key: string]: any
} | RadioGroupValue

export interface RadioGroupProps<T extends RadioGroupItem = RadioGroupItem> extends Pick<RadioGroupRootProps, 'defaultValue' | 'disabled' | 'loop' | 'modelValue' | 'name' | 'required'> {
  as?: string | Component
  valueKey?: string
  items?: T[]
  variant?: 'list' | 'card'
  color?: Color
  indicatorPosition?: 'start' | 'end' | 'hidden'
  orientation?: 'horizontal' | 'vertical'
  fill?: boolean
  class?: string | object
  activeBackground?: boolean
}

export type RadioGroupEmits = RadioGroupRootEmits & {
  change: [payload: Event]
}

type SlotProps<T extends RadioGroupItem> = (props: { item: T & { id: string }, modelValue?: RadioGroupValue }) => any

export interface RadioGroupSlots<T extends RadioGroupItem = RadioGroupItem> {
  label?: SlotProps<T>
  description?: SlotProps<T>
}

const ui = {
  root: 'relative',
  fieldset: 'flex',
  item: '',
  orientation: {
    horizontal: {
      fieldset: 'flex-row',
    },
    vertical: {
      fieldset: 'flex-col',
    },
  },
  variant: {
    list: {
      orientation: {
        horizontal: {
          fieldset: 'gap-x-3',
        },
        vertical: {
          fieldset: 'gap-y-3',
        },
      },
    },
    card: {
      orientation: {
        horizontal: {
          fieldset: 'gap-x-1',
        },
        vertical: {
          fieldset: 'gap-y-1',
        },
      },
    },
  },
}

const radioUi = {
  root: 'relative flex has-[[disabled]]:cursor-not-allowed has-[[disabled]]:hover:border-secondary-200 has-[[disabled]]:opacity-30',
  container: 'flex items-center',
  base: 'size-4 rounded-full ring-inset ring-1px overflow-hidden transition-colors data-[disabled]:cursor-not-allowed',
  start: {
    root: 'flex-row',
    base: 'me-2',
    wrapper: '',
  },
  end: {
    root: 'flex-row-reverse justify-between',
    base: 'ms-2',
    wrapper: '',
  },
  hidden: {
    root: '',
    base: 'sr-only',
    wrapper: 'w-full',
  },
  wrapper: '',
  label: 'block font-semibold text-md',
  description: 'text-muted text-sm text-secondary-800 mt-0.5',
  variant: {
    card: {
      root: 'bg-white px-3.5 py-3 border border-muted rounded-lg cursor-pointer items-center hover:border-secondary-500 has-[[data-active]]:border-{color}-500 has-[[data-active]]:hover:border-{color}-500 has-[[data-active]]:ring-1px has-[[data-active]]:ring-{color}-200',
    },
    list: {
      root: 'items-start',
    },
  },
  disabled: {
    base: 'cursor-not-allowed bg-secondary-200 ring-secondary-600 data-[active]:ring-4px',
    label: 'cursor-not-allowed',
    variant: {
      card: {
        root: 'cursor-not-allowed',
      },
    },
  },
  color: {
    base: 'bg-white not-disabled:hover:bg-secondary-100 ring-secondary-600 data-[active]:ring-4px data-[active]:ring-{color}-500 data-[active]:hover:ring-{color}-600',
    secondary: 'bg-white not-disabled:hover:bg-secondary-100 ring-secondary-600 data-[active]:ring-4px data-[active]:ring-secondary-900 data-[active]:hover:ring-black',
    variant: {
      card: {
        root: '',
      },
    },
  },
}
</script>

<script setup lang="ts" generic="T extends RadioGroupItem">
import { reactivePick } from '@vueuse/core'
import { Label, RadioGroupIndicator, RadioGroupRoot, RadioGroupItem as RekaRadioGroupItem, useForwardPropsEmits } from 'reka-ui'
import { computed, useId } from 'vue'

const props = withDefaults(defineProps<RadioGroupProps<T>>(), {
  variant: 'list',
  indicatorPosition: 'start',
  color: 'primary',
  valueKey: 'value',
  orientation: 'vertical',
  activeBackground: false,
})
const emits = defineEmits<RadioGroupEmits>()
const slots = defineSlots<RadioGroupSlots<T>>()

const rootProps = useForwardPropsEmits(reactivePick(props, 'as', 'modelValue', 'defaultValue', 'loop', 'required'), emits)

const id = useId()

function normalizeItem(item: any) {
  if (item === null) {
    return {
      id: `${id}:null`,
      value: undefined,
      label: undefined,
    }
  }

  if (typeof item === 'string' || typeof item === 'number') {
    return {
      id: `${id}:${item}`,
      value: String(item),
      label: String(item),
    }
  }

  const value = item[props.valueKey]
  const label = item.label
  const description = item.description

  return {
    ...item,
    value,
    label,
    description,
    id: `${id}:${value}`,
  }
}

const normalizedItems = computed(() => {
  if (!props.items) {
    return []
  }

  return props.items.map(normalizeItem)
})

function onUpdate(value: any) {
  // @ts-expect-error - 'target' does not exist in type 'EventInit'
  const event = new Event('change', { target: { value } })
  emits('change', event)
}

const radioRootClass = computed(() => [
  radioUi.root,
  radioUi[props.indicatorPosition].root,
  radioUi.variant[props.variant].root.replaceAll('{color}', props.color),
  props.variant === 'card' && props.disabled && radioUi.disabled.variant.card.root,
  props.fill && 'flex-1',
])
const radioBaseClass = computed(() => [
  radioUi.base,
  radioUi[props.indicatorPosition].base,
  props.disabled && radioUi.disabled.base,
  !props.disabled && (props.color === 'secondary' ? radioUi.color.secondary : radioUi.color.base.replaceAll('{color}', props.color)),
])
const radioWrapperClass = computed(() => [radioUi.wrapper, radioUi[props.indicatorPosition].wrapper])
const radioLabelClass = computed(() => [radioUi.label, props.disabled && radioUi.disabled.label])
</script>

<template>
  <RadioGroupRoot
    :id
    v-slot="{ modelValue }"
    v-bind="rootProps"
    :name
    :disabled
    :class="[ui.root, props.class]"
    @update:model-value="onUpdate"
  >
    <fieldset :class="[ui.fieldset, ui.orientation[props.orientation].fieldset, ui.variant[props.variant].orientation[props.orientation].fieldset]">
      <component :is="(!variant || variant === 'list') ? 'div' : Label" v-for="item in normalizedItems" :key="item.value" :class="[radioRootClass, props.activeBackground && props.color ? `has-[[data-active]]:!bg-${props.color}-50` : '']">
        <div :class="radioUi.container">
          <RekaRadioGroupItem
            :id="item.id"
            :value="item.value"
            :disabled="item.disabled"
            :class="radioBaseClass"
          >
            <RadioGroupIndicator />
          </RekaRadioGroupItem>
        </div>

        <div v-if="(item.label || !!slots.label) || (item.description || !!slots.description)" :class="radioWrapperClass">
          <component :is="(!variant || variant === 'list') ? Label : 'p'" v-if="item.label || !!slots.label" :for="item.id" :class="radioLabelClass">
            <slot name="label" :item="item" :model-value="(modelValue as RadioGroupValue)">
              {{ item.label }}
            </slot>
          </component>
          <p v-if="item.description || !!slots.description" :class="radioUi.description">
            <slot name="description" :item="item" :model-value="(modelValue as RadioGroupValue)">
              {{ item.description }}
            </slot>
          </p>
        </div>
      </component>
    </fieldset>
  </RadioGroupRoot>
</template>
